import { ReactElement, useMemo } from 'react'
import { SceneLineMarker } from '@scene'
import { DoubleSide } from 'three'
import { grey } from '@mui/material/colors'
import type { InnerForcesEnvelopeDTO } from '@api/fastAPI.schemas'
import { useAppStore } from '@stores'
import { useControlStore } from '@editorStores'
import { makeXDirectionPositive } from 'src/components/scene/utils'
import EnvelopeExtremeLabelStack from './EnvelopeExtremeLabelStack'
import EnvelopeTitleLabel from './EnvelopeTitleLabel'
import {
  buildEnvelopeGeometry,
  ENVELOPE_MAX_COLOR,
  ENVELOPE_MIN_COLOR,
  envelopeComponentLabels,
  envelopeComponentUnits,
  toPositionArray,
  type EnvelopeComponent,
} from './utils'

const AXIS_COLOR = grey[800]
const FILL_OPACITY = 0.25

interface Props {
  envelope: InnerForcesEnvelopeDTO
  component: EnvelopeComponent
}

/**
 * Design inner forces envelope (max in blue, min in red, as in the inner forces plot) of the
 * selected member, drawn in the vertical plane through the member axis with value labels (styled
 * like the 3D line load labels) at the extremes of each curve per field, both values stacked where
 * an extreme sits on a jump. Hidden in the floorplan view, where the vertical diagram would collapse to a line.
 */
const InnerForcesEnvelopeMesh = ({ envelope, component }: Props): ReactElement | null => {
  const storey = useControlStore(state => state.floorplanState.storey)
  const maxOrdinate = useAppStore(state => state.loadRenderConfig.innerForcesEnvelope.maxOrdinate)

  const diagram = useMemo(() => {
    const geometry = buildEnvelopeGeometry(envelope, component, maxOrdinate)
    if (!geometry) return null
    return {
      geometry,
      maxFillPositions: toPositionArray(geometry.max.fillTriangles),
      minFillPositions: toPositionArray(geometry.min.fillTriangles),
      // text in the diagram plane, reading along the member like the 3D line load labels and
      // standing upright along the ordinates
      textXDirection: makeXDirectionPositive(
        geometry.axis[1].clone().sub(geometry.axis[0]).normalize(),
      ),
    }
  }, [envelope, component, maxOrdinate])

  if (storey !== null || !diagram) return null
  const { geometry, maxFillPositions, minFillPositions, textXDirection } = diagram
  const textYDirection = geometry.ordinateDirection

  const unit = envelopeComponentUnits[component]
  const { maxExtremes, minExtremes } = geometry

  return (
    <>
      {[
        { positions: maxFillPositions, color: ENVELOPE_MAX_COLOR },
        { positions: minFillPositions, color: ENVELOPE_MIN_COLOR },
      ].map(({ positions, color }) => (
        <mesh key={color} renderOrder={998}>
          {/* declared in JSX so R3F disposes the GPU buffers on unmount; keyed on the ordinate
              scale because R3F does not re-upload a buffer attribute whose array changes */}
          <bufferGeometry key={maxOrdinate}>
            <bufferAttribute
              attach="attributes-position"
              array={positions}
              count={positions.length / 3}
              itemSize={3}
            />
          </bufferGeometry>
          <meshBasicMaterial
            color={color}
            transparent
            opacity={FILL_OPACITY}
            side={DoubleSide}
            depthWrite={false}
          />
        </mesh>
      ))}
      <SceneLineMarker lines={[geometry.axis]} color={AXIS_COLOR} lineWidth={2} disableDepthTest />
      <SceneLineMarker lines={[geometry.max.outline]} color={ENVELOPE_MAX_COLOR} disableDepthTest />
      <SceneLineMarker lines={[geometry.min.outline]} color={ENVELOPE_MIN_COLOR} disableDepthTest />
      {[
        { extremes: maxExtremes, color: ENVELOPE_MAX_COLOR },
        { extremes: minExtremes, color: ENVELOPE_MIN_COLOR },
      ].map(({ extremes, color }) =>
        extremes.map(extreme => (
          <EnvelopeExtremeLabelStack
            key={`${color}-${extreme.relativePosition}`}
            extreme={extreme}
            unit={unit}
            color={color}
            textXDirection={textXDirection}
            textYDirection={textYDirection}
          />
        )),
      )}
      <EnvelopeTitleLabel
        title={envelopeComponentLabels[component]}
        axis={geometry.axis}
        ordinateDirection={geometry.ordinateDirection}
        maxOrdinate={maxOrdinate}
        textXDirection={textXDirection}
      />
    </>
  )
}

export default InnerForcesEnvelopeMesh
